// ─────────────────────────────────────────────────────
// Percoro API Utility
// Permanent fix for #29 — Token expiration handling
//
// KEY CHANGE: validateSession() now decodes the JWT
// locally instead of calling /auth/validate every time.
// This eliminates the CORS/authorizer failures that
// caused the recurring "logged in but API fails" bug.
// ─────────────────────────────────────────────────────

const API_URL = process.env.REACT_APP_API_URL || 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/prod';

// ─── JWT Helpers ─────────────────────────────────────

/**
 * Decode a JWT payload without a library.
 * Returns null if the token is malformed.
 */
const decodeJwtPayload = (token: string): Record<string, any> | null => {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const payload = parts[1];
        // Base64url → Base64 → JSON
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const json = atob(base64);
        return JSON.parse(json);
    } catch {
        return null;
    }
};

/**
 * Check if a JWT is expired (with 60-second buffer).
 * Returns true if expired or unparseable.
 */
const isTokenExpired = (token: string): boolean => {
    const payload = decodeJwtPayload(token);
    if (!payload || !payload.exp) return true;
    const now = Math.floor(Date.now() / 1000);
    return payload.exp < now + 60; // 60-second buffer
};

/**
 * Extract user info from the idToken payload.
 */
const getUserFromToken = (token: string): Record<string, any> | null => {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;

  // Preserve existing userId from localStorage if available (database user_id)
  const existingUser = localStorage.getItem('user');
  let existingUserId = null;
  if (existingUser) {
      try {
          existingUserId = JSON.parse(existingUser).userId;
      } catch {}
  }

  return {
      userId: existingUserId || payload.sub,
      email: payload.email,
      name: payload.name || payload['custom:name'] || payload.email,
      emailVerified: payload.email_verified,
  };
};

// ─── Auth Functions ──────────────────────────────────

export const getAuthToken = (): string | null => {
    return localStorage.getItem('idToken');
};

export const isAuthenticated = (): boolean => {
    const token = getAuthToken();
    if (!token) return false;
    return !isTokenExpired(token);
};

export const logout = (message?: string) => {
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    if (message) {
        sessionStorage.setItem('authMessage', message);
    }
    window.location.href = '/login';
};

/**
 * Refresh tokens using the Cognito refreshToken.
 * Returns true if refresh succeeded.
 */
export const refreshTokens = async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return false;

    try {
        const response = await fetch(`${API_URL}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
        });

        if (response.ok) {
            const data = await response.json();
            if (data.success && data.idToken) {
                localStorage.setItem('idToken', data.idToken);
                if (data.accessToken) localStorage.setItem('accessToken', data.accessToken);
                // Update stored user data from new token
                const user = getUserFromToken(data.idToken);
                if (user) localStorage.setItem('user', JSON.stringify(user));
                return true;
            }
        }
        return false;
    } catch (error) {
        console.error('Token refresh failed:', error);
        return false;
    }
};

/**
 * Validate the current session.
 *
 * *** THIS IS THE #29 FIX ***
 *
 * OLD behavior: Called GET /auth/validate on EVERY page load.
 *   → If CORS failed, authorizer blocked, or network hiccup: session killed.
 *   → User appeared "logged in" but all API calls returned 401.
 *
 * NEW behavior: Decodes the JWT locally to check expiration.
 *   → No API call needed for validation.
 *   → If token is expired, tries refresh. If refresh fails, THEN invalidate.
 *   → If token is valid, extracts user info directly from the JWT payload.
 *   → Network issues can NEVER kill a valid session.
 */
export const validateSession = async (): Promise<{ valid: boolean; user?: any }> => {
    const token = getAuthToken();

    if (!token) {
        return { valid: false };
    }

    // Step 1: Check if token is expired locally (no API call!)
    if (!isTokenExpired(token)) {
        // Token is still valid — extract user from JWT payload
        const user = getUserFromToken(token);
        if (user) {
            localStorage.setItem('user', JSON.stringify(user));
            return { valid: true, user };
        }
    }

    // Step 2: Token is expired — try to refresh
    const refreshed = await refreshTokens();
    if (refreshed) {
        const newToken = getAuthToken();
        if (newToken) {
            const user = getUserFromToken(newToken);
            if (user) {
                return { valid: true, user };
            }
        }
    }

    // Step 3: Refresh failed — session is truly invalid
    return { valid: false };
};

// ─── API Request Wrapper ─────────────────────────────

export const apiRequest = async (
    endpoint: string,
    options: RequestInit = {},
    retry = true
): Promise<Response> => {
    let token = getAuthToken();

    // Pre-flight: if token is expired, try refresh BEFORE the request
    if (token && isTokenExpired(token)) {
        const refreshed = await refreshTokens();
        if (refreshed) {
            token = getAuthToken();
        } else {
            // Can't refresh — let the request go through anyway,
            // the 401 handler below will catch it
        }
    }

    const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
    };

    if (token) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers,
    });

    // Handle 401 Unauthorized — try token refresh once
    if (response.status === 401 && retry) {
        const refreshed = await refreshTokens();

        if (refreshed) {
            // Retry the original request with new token
            return apiRequest(endpoint, options, false);
        }

        // Refresh failed — logout
        logout('Session expired. Please log in again.');
        throw new Error('Session expired. Please log in again.');
    }

    return response;
};

// ─── Convenience Methods ─────────────────────────────

export const api = {
    get: (endpoint: string) => apiRequest(endpoint, { method: 'GET' }),
    post: (endpoint: string, data: unknown) =>
        apiRequest(endpoint, {
            method: 'POST',
            body: JSON.stringify(data),
        }),
    put: (endpoint: string, data: unknown) =>
        apiRequest(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),
    delete: (endpoint: string) => apiRequest(endpoint, { method: 'DELETE' }),
};

// ─── Notification Action Helpers (#211 Phase 2 + Phase 5) ──────

/**
 * Records a click on a notification (admin broadcast OR lifecycle notification).
 * For all categories, the click also dismisses the notification for this user.
 * For click-only categories (security/maintenance/site_status), the click IS
 * the only path to dismissal.
 *
 * The endpoint is the same for both notification kinds — the backend branches
 * on table lookup. UUIDs don't collide.
 *
 * Backend: POST /notifications/{id}/click — see backend/src/handlers/notifications/user-actions.ts
 * Returns 200 on success, 404 if not found, 410 if recalled.
 */
export const clickNotification = async (notificationId: string): Promise<Response> => {
    return apiRequest(`/notifications/${encodeURIComponent(notificationId)}/click`, {
        method: 'POST',
    });
};

/**
 * Dismisses a notification via the X button (admin broadcast OR lifecycle).
 * Returns 403 for click-only admin categories (security/maintenance/site_status) —
 * the frontend should not render the X button for those categories, but the
 * server enforces independently. Lifecycle notifications are always X-dismissable.
 *
 * Backend: POST /notifications/{id}/dismiss — see backend/src/handlers/notifications/user-actions.ts
 * Returns 200 on success, 403 if click-only, 404 if not found.
 */
export const dismissNotification = async (notificationId: string): Promise<Response> => {
    return apiRequest(`/notifications/${encodeURIComponent(notificationId)}/dismiss`, {
        method: 'POST',
    });
};

/**
 * Phase 5 (#211): semantic alias for dismissing a lifecycle notification.
 * The endpoint is shared with admin broadcasts (Option A — backend branches
 * on table lookup), so this is just dismissNotification under a clearer name
 * for callers operating on lifecycle items. If we ever split the endpoints,
 * this is the call site to update.
 */
export const dismissLifecycleNotification = dismissNotification;

// ─── Legacy Nudge Dismissal (server-side, Day 50) ─────
// Canonical nudge key. MUST match backend utils/nudges.ts nudgeKey():
//   `${category}:${tripId || 'global'}`
export const nudgeKeyFor = (category: string, tripId?: string | null): string =>
    `${category}:${tripId || 'global'}`;

// Records a permanent server-side dismissal of a legacy nudge.
// Backend: POST /users/me/nudges/dismiss (handlers/users/nudges.ts).
export const dismissNudge = async (
    nudgeKey: string,
    triggerState = ''
): Promise<Response> => {
    return apiRequest('/users/me/nudges/dismiss', {
        method: 'POST',
        body: JSON.stringify({ nudge_key: nudgeKey, trigger_state: triggerState }),
    });
};

export { API_URL };
